import React from 'react';
import { ScheduledCommandPanelModelProps } from './modelTypes';

const ScheduledCommandPanelToolbar: React.FC<ScheduledCommandPanelModelProps> = ({ model }) => {
  const {
    activeView,
    createRequest,
    density,
    error,
    laneCounts,
    load,
    newRequest,
    requestLaneFilter,
    setActiveView,
    setDensity,
    setMyQueueOnly,
    setNewRequest,
    setRequestLaneFilter,
    setSeatPlanDate,
    setShowComposer,
    showComposer,
  } = model;

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#0b1630_0%,#14315f_46%,#0a4b7a_100%)] p-5 text-white shadow-[0_14px_35px_rgba(15,23,42,0.24)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cyan-200/80">Command Chain</p>
            <h3 className="text-2xl font-black tracking-tight">Seat Inventory Control</h3>
          </div>
          <button
            onClick={load}
            className="rounded-xl border border-cyan-200/40 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-white hover:bg-white/20"
          >
            Refresh
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { id: 'OPEN', label: 'Open', count: laneCounts.OPEN },
            { id: 'OFFER', label: 'Offer', count: laneCounts.OFFER },
            { id: 'PENDING', label: 'Approval', count: laneCounts.PENDING },
            { id: 'CLOSED', label: 'Closed', count: laneCounts.CLOSED },
          ].map((lane) => (
            <button
              key={lane.id}
              onClick={() => {
                setRequestLaneFilter((prev) => (prev === lane.id ? 'ALL' : (lane.id as any)));
                setActiveView('REQUESTS');
              }}
              className={`rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-left ${requestLaneFilter === lane.id ? 'ring-2 ring-cyan-300' : ''}`}
            >
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-200">{lane.label}</p>
              <p className="mt-1 text-xl font-black">{lane.count}</p>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">{error}</div>}

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'OPERATIONS', label: 'Operations' },
          { id: 'REQUESTS', label: 'Requests' },
          { id: 'INVENTORY', label: 'Inventory' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id as any)}
            className={`rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.16em] ${
              activeView === tab.id
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={() => setShowComposer((v) => !v)}
          className={`ml-auto rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.16em] ${
            showComposer ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600'
          }`}
        >
          {showComposer ? 'Hide Quick Actions' : 'Show Quick Actions'}
        </button>
      </div>

      <div className="sticky top-2 z-10 rounded-2xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setActiveView('REQUESTS')} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-700 hover:border-indigo-300 hover:text-indigo-700">Work Requests</button>
          <button onClick={() => setActiveView('OPERATIONS')} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-700 hover:border-indigo-300 hover:text-indigo-700">Open Calendar</button>
          <button onClick={() => setShowComposer(v => !v)} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-slate-700 hover:border-indigo-300 hover:text-indigo-700">{showComposer ? 'Hide Forms' : 'Quick Add'}</button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Density</span>
            <button onClick={() => setDensity('COMFORT')} className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${density === 'COMFORT' ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'}`}>Comfort</button>
            <button onClick={() => setDensity('DENSE')} className={`rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${density === 'DENSE' ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'}`}>Dense</button>
          </div>
        </div>
      </div>

      {showComposer && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Create Seat Request</p>
            <div className="grid grid-cols-2 gap-2">
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Customer name" value={newRequest.customerName} onChange={e => setNewRequest(v => ({ ...v, customerName: e.target.value }))} />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Email" value={newRequest.fromEmail} onChange={e => setNewRequest(v => ({ ...v, fromEmail: e.target.value }))} />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm uppercase" placeholder="From" value={newRequest.fromCode} onChange={e => setNewRequest(v => ({ ...v, fromCode: e.target.value.toUpperCase() }))} />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm uppercase" placeholder="To" value={newRequest.toCode} onChange={e => setNewRequest(v => ({ ...v, toCode: e.target.value.toUpperCase() }))} />
              <input type="date" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={newRequest.travelDate} onChange={e => setNewRequest(v => ({ ...v, travelDate: e.target.value }))} />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Time (10:00)" value={newRequest.preferredTime} onChange={e => setNewRequest(v => ({ ...v, preferredTime: e.target.value }))} />
              <input type="number" min={1} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="PAX" value={newRequest.pax} onChange={e => setNewRequest(v => ({ ...v, pax: Math.max(1, Number(e.target.value) || 1) }))} />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm col-span-1" placeholder="Notes" value={newRequest.notes} onChange={e => setNewRequest(v => ({ ...v, notes: e.target.value }))} />
            </div>
            <button onClick={createRequest} className="rounded-xl bg-indigo-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-white hover:bg-indigo-500">
              Add Request
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Schedule Source Of Truth</p>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
              <p className="text-sm font-black text-indigo-800">Create scheduled flights in System Registry / Routes</p>
              <p className="mt-1 text-xs font-semibold text-indigo-700">
                Operators do not build flight slots here. They only view date availability, process demand, and sell seats.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveView('OPERATIONS');
                    if (new Date().toISOString().slice(0, 10)) setSeatPlanDate(new Date().toISOString().slice(0, 10));
                  }}
                  className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700"
                >
                  Open Calendar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.setItem('ma_admin_open_subtab', 'ROUTES');
                    } catch {
                      // ignore
                    }
                    window.location.hash = '#/admin';
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700"
                >
                  Go To System Registry / Routes
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
              Required fields for registry flight setup:
              <span className="font-semibold"> route, aircraft type, departure time, departure date/day pattern, seat capacity, price policy.</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduledCommandPanelToolbar;
